import { Box, Chip, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Base_Unit_Type_Enum, Billing_Type_Enum, Measurement_Unit_Enum } from '@kheops/graphql';
import { FormatMeasurementUnit, FormatPackagingUnitPrice, FormatPackagingVolumePrice, formatPriceByBillingType, formatSuggestedRetailPrice, getConvertedSuggestedRetailPrice, round } from '@kheops/utils';
import useCurrencyFormat from '../../hooks/useCurrencyFormat';
import { formatPackagingPriceIncludingDescriptor, getBaseUnitExcludedTaxMargin } from '../../common/utils/common.utils';
import { BaseUnit, Packaging } from './PackagingPage';

type PackagingPagePricesBaseUnit = Pick<
  BaseUnit,
  'billing_type'
  | 'content_measurement_unit'
  | 'suggested_retail_price'
  | 'unit_net_content'
  | 'unit_quantity'
  | 'unit_type'
>;

export interface PackagingPagePricesProps {
  baseUnit: PackagingPagePricesBaseUnit;
  packaging: Packaging;
  vatRate: number;
}

export default function PackagingPagePrices({ baseUnit, packaging, vatRate }: PackagingPagePricesProps): React.JSX.Element {
  const { t } = useTranslation(['products', 'price']);
  const currencyFormat = useCurrencyFormat();
  let suggestedRetailPriceMargin: number;
  let formattedSuggestedRetailPrice: string | undefined;

  const unitPrice = useMemo((): string => {
    return `${formatPriceByBillingType(currencyFormat.format(round(FormatPackagingUnitPrice(packaging))), baseUnit.billing_type)}`;
  }, [baseUnit, packaging]);

  const volumePrice = useMemo((): string => {
    const price = FormatPackagingVolumePrice(packaging, baseUnit);

    return `${currencyFormat.format(round(price!.value))}/${FormatMeasurementUnit(price!.unit, 'fr', 1)}`;
  }, [baseUnit, packaging]);

  if (baseUnit.suggested_retail_price) {
    const baseUnitInfo = {
      suggestedRetailPrice: baseUnit.suggested_retail_price,
      billingType: baseUnit.billing_type,
      netContent: (baseUnit.unit_net_content || packaging.net_content) as number,
      unitType: baseUnit.unit_type!,
      measurementUnit: (baseUnit.content_measurement_unit || packaging.content_measurement_unit) as Measurement_Unit_Enum,
    };

    formattedSuggestedRetailPrice = formatSuggestedRetailPrice(baseUnitInfo);
    suggestedRetailPriceMargin = getBaseUnitExcludedTaxMargin(
      baseUnit.billing_type === Billing_Type_Enum.WeightDependent && baseUnit.unit_type !== Base_Unit_Type_Enum.Bulk
        ? getConvertedSuggestedRetailPrice(baseUnit.suggested_retail_price, baseUnitInfo.netContent, baseUnitInfo.measurementUnit)
        : baseUnit.suggested_retail_price,
      FormatPackagingUnitPrice(packaging!),
      vatRate,
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5 }}>
        <Typography variant="titleLarge">
          {baseUnit.unit_type === Base_Unit_Type_Enum.Bulk ? volumePrice : unitPrice}
        </Typography>
        {formattedSuggestedRetailPrice && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                sm: 'row',
              },
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Typography variant="bodyLarge" color="onSurfaceVariant">
              {t('price:suggest_retail_price_acronym')} : {formattedSuggestedRetailPrice}
            </Typography>
            <Chip variant="outlinedTag" label={t('price:percentage_of_margin', { margin: suggestedRetailPriceMargin! })} />
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'end', gap: 0.5 }}>
        <Typography variant="bodyMedium">
          {formatPackagingPriceIncludingDescriptor(t, `${formatPriceByBillingType(currencyFormat.format(round(packaging.price)), baseUnit.billing_type)}`, packaging.trade_item_unit_descriptor)}
        </Typography>
        {baseUnit.unit_type !== Base_Unit_Type_Enum.Bulk && (
          <Typography variant="bodyMedium" color="onSurfaceVariant">
            {volumePrice}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
